import type { Topic, TopicAction, EntityType, WorkflowType } from '@communities/content-management/domain';
import {
  useCommunityWorkflows,
  useCommunityGroupWorkflows,
  useCommunityTopics,
  useCommunityGroupTopics,
  useCommunityActions,
  useCommunityGroupActions,
  useUpsertCommunityWorkflow,
  useUpsertCommunityGroupWorkflow,
} from '@communities/content-management/hooks';
import { isCommunity } from '@communities/content-management/utils';
import { ErrorBanner } from '@core/components';
import { LoadingSpinner, Select, MultiSelect, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect, useCallback } from 'react';

interface TopicActionDialogProps {
  topicAction?: TopicAction | undefined;
  entityId: string;
  entityType: EntityType;
  workflowType: WorkflowType;
  isOpen: boolean;
  onClose: () => void;
  isTopicConfigured?: (topicName: string, excludeTopicName?: string) => boolean;
  getUnusedTopics?: () => Topic[];
}

/**
 * Dialog component for creating or editing a topic action
 * Simplified to use only controlled mode
 */
// eslint-disable-next-line complexity, max-statements
export function TopicActionDialog({
  topicAction: initialTopicAction,
  entityId,
  entityType,
  workflowType,
  isOpen,
  onClose,
  isTopicConfigured,
  getUnusedTopics,
}: TopicActionDialogProps) {
  const isForCommunity = isCommunity(entityType);

  // Hooks for workflows
  const communityWorkflowsHook = useCommunityWorkflows(isForCommunity ? entityId : undefined, workflowType);
  const communityGroupWorkflowsHook = useCommunityGroupWorkflows(!isForCommunity ? entityId : undefined, workflowType);

  // Hooks for topics
  const communityTopicsHook = useCommunityTopics(isForCommunity ? entityId : undefined);
  const communityGroupTopicsHook = useCommunityGroupTopics(!isForCommunity ? entityId : undefined);

  // Hooks for actions
  const communityActionsHook = useCommunityActions(isForCommunity ? entityId : undefined);
  const communityGroupActionsHook = useCommunityGroupActions(!isForCommunity ? entityId : undefined);

  // Hooks for upserting workflows
  const communityUpsertWorkflowHook = useUpsertCommunityWorkflow();
  const communityGroupUpsertWorkflowHook = useUpsertCommunityGroupWorkflow(!isForCommunity ? entityId : undefined);

  const { workflows: workflowConfig } = isForCommunity ? communityWorkflowsHook : communityGroupWorkflowsHook;
  const { upsertWorkflow, loading: updateLoading, errors, resetErrors } = isForCommunity ? communityUpsertWorkflowHook : communityGroupUpsertWorkflowHook;

  const topics = isForCommunity ? communityTopicsHook.topics : communityGroupTopicsHook.topics;
  const actions = isForCommunity ? communityActionsHook.actions : communityGroupActionsHook.actions;
  const topicActions = workflowConfig?.topicActions || [];

  const [currentTopicAction, setCurrentTopicAction] = useState<TopicAction | undefined>(initialTopicAction);

  // Generic function to update topic action fields
  const updateTopicAction = useCallback((updates: Partial<TopicAction>) => {
    setCurrentTopicAction(current => {
      // Handle case when currentTopicAction is undefined
      const base = current || { topicName: '', actionNames: [] };
      const updated = { ...base, ...updates };

      return updated;
    });
  }, []);

  // Get available topics for the current editing context
  const getAvailableTopics = useCallback((): Topic[] => {
    if (initialTopicAction && isTopicConfigured) {
      // When editing, include the current topic and all unconfigured topics
      const currentTopicName = initialTopicAction.topicName;

      return topics.filter(topic => !isTopicConfigured(topic.name, currentTopicName) || topic.name === currentTopicName);
    } else {
      // When adding new, only show unconfigured topics
      return getUnusedTopics ? getUnusedTopics() : topics;
    }
  }, [initialTopicAction, isTopicConfigured, topics, getUnusedTopics]);

  // Initialize or reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetErrors();

      // Initialize with initialTopicAction or create a default one
      if (initialTopicAction) {
        setCurrentTopicAction(initialTopicAction);
      } else {
        // Create a default topic action with empty values
        setCurrentTopicAction({ topicName: '', actionNames: [] });

        // If topics are available, select the first one by default
        const availableTopics = getAvailableTopics();

        if (availableTopics.length > 0) {
          setTimeout(() => {
            updateTopicAction({ topicName: availableTopics[0].name });
          }, 0);
        }
      }
    }
  }, [initialTopicAction, isOpen, resetErrors, getAvailableTopics, updateTopicAction]);

  const handleSubmit = async () => {
    resetErrors();

    // Create a clean topic action object without Apollo metadata
    const cleanTopicAction: TopicAction = {
      topicName: currentTopicAction?.topicName || '',
      actionNames: currentTopicAction?.actionNames || [],
    };

    // Find existing topic action or create new one
    const existingIndex = topicActions.findIndex((ta: TopicAction) => ta.topicName === cleanTopicAction.topicName);

    let newTopicActions: TopicAction[];

    if (existingIndex >= 0) {
      // Update existing
      newTopicActions = [...topicActions];
      newTopicActions[existingIndex] = cleanTopicAction;
    } else {
      // Add new
      newTopicActions = [...topicActions, cleanTopicAction];
    }

    // Create updated workflow configuration that preserves existing settings
    const updatedConfig = workflowConfig
      ? {
          // Preserve all existing properties
          ...workflowConfig,
          // Only update the topic actions
          topicActions: newTopicActions,
        }
      : {
          // If no existing config, create a new one with defaults
          // Include type in the workflow configuration object as it's required by the interface
          type: workflowType,
          topicActions: newTopicActions,
          // Communities need API settings, community groups don't
          ...(isForCommunity && {
            apiSettings: {
              maxTps: 100,
              accessClients: [],
            },
          }),
        };

    const success = await upsertWorkflow(entityId, workflowType, updatedConfig);

    if (success) {
      onClose();
    }
    // If not successful, the error state in the hook will be set
  };

  const actionOptions = actions.map(action => ({
    id: action.name,
    label: action.name,
    value: action.name,
  }));

  return (
    <Dialog
      description={initialTopicAction ? 'Update topic action configuration' : 'Configure actions for a topic'}
      isOpen={isOpen}
      primaryAction={{
        label: updateLoading ? <LoadingSpinner /> : initialTopicAction ? 'Update Topic Action' : 'Create Topic Action',
        onClick: handleSubmit,
        disabled: updateLoading,
      }}
      size="lg"
      title={initialTopicAction ? 'Edit Topic Action' : 'Add Topic Action'}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the topic action:" />}

        {/* Topics Select */}
        <Select
          disabled={updateLoading}
          options={getAvailableTopics().map(topic => ({ label: topic.name, value: topic.name }))}
          title="Topic"
          value={currentTopicAction?.topicName || ''}
          onChange={value => updateTopicAction({ topicName: value })}
        />

        {/* Actions Multi Select */}
        <MultiSelect
          disabled={updateLoading}
          options={actionOptions}
          placeholder="Select actions"
          selectedValues={currentTopicAction?.actionNames || []}
          title="Actions"
          onSelectionChange={selectedActions => updateTopicAction({ actionNames: selectedActions })}
        />
      </div>
    </Dialog>
  );
}
